import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@cleanbudget/ui/components/field";
import { Input } from "@cleanbudget/ui/components/input";
import { NativeSelect, NativeSelectOption } from "@cleanbudget/ui/components/native-select";
import { Switch } from "@cleanbudget/ui/components/switch";
import { Textarea } from "@cleanbudget/ui/components/textarea";
import { cn } from "@cleanbudget/ui/lib/utils";
import { IconAlertCircle, IconLoader2 } from "@tabler/icons-react";
import { type ComponentProps, type ReactNode, useId, useMemo } from "react";
import {
	type Control,
	type FieldPath,
	type FieldValues,
	FormProvider,
	type SubmitHandler,
	type UseFormProps,
	type UseFormReturn,
	useController,
	useForm,
	useFormState,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { validationMessage, zodResolver } from "./zod-resolver";

/**
 * Forms on react-hook-form with the contract's Zod schemas:
 *
 * ```tsx
 * const form = useZodForm(CreateEmployeeInput, { defaultValues: { name: "", email: "" } });
 * <Form form={form} onSubmit={(values) => create.mutateAsync(values)}>
 *   <TextField control={form.control} name="email" label={t("email")} type="email" />
 *   <FormError control={form.control} />
 *   <SubmitButton control={form.control}>{t("save")}</SubmitButton>
 * </Form>
 * ```
 *
 * Fields check themselves when they lose focus, then on every change; everything on submit. An
 * error the API sends back goes on its field (`form.setError("email", ...)`) or, when it belongs
 * to no field, on `root` (shown by `FormError`). Read values with `useWatch`, not `watch()`.
 */
export function useZodForm<S extends z.ZodType<FieldValues, FieldValues>>(
	schema: S,
	options: Omit<UseFormProps<z.input<S>, unknown, z.output<S>>, "resolver"> = {},
): UseFormReturn<z.input<S>, unknown, z.output<S>> {
	const { t } = useTranslation();
	const resolver = useMemo(() => zodResolver(schema, (issue) => validationMessage(issue, t)), [schema, t]);
	return useForm<z.input<S>, unknown, z.output<S>>({ mode: "onTouched", ...options, resolver });
}

export function Form<TInput extends FieldValues, TOutput>({
	form,
	onSubmit,
	children,
	className,
}: {
	form: UseFormReturn<TInput, unknown, TOutput>;
	onSubmit: SubmitHandler<TOutput>;
	children: ReactNode;
	className?: string;
}) {
	return (
		<FormProvider {...form}>
			<form
				noValidate
				onSubmit={form.handleSubmit(onSubmit)}
				className={cn("flex flex-col gap-5", className)}
			>
				{children}
			</form>
		</FormProvider>
	);
}

/** A labelled text input bound to one field, with its help text and error. */
export function TextField<T extends FieldValues>({
	control,
	name,
	label,
	description,
	...input
}: {
	control: Control<T, unknown, unknown>;
	name: FieldPath<T>;
	label: ReactNode;
	description?: ReactNode;
} & Omit<ComponentProps<"input">, "name" | "value" | "defaultValue" | "onChange" | "onBlur">) {
	const { field, fieldState } = useController({ control, name });
	const id = useId();
	const describedBy = [description ? `${id}-description` : "", fieldState.error ? `${id}-error` : ""]
		.filter(Boolean)
		.join(" ");

	return (
		<Field data-invalid={fieldState.invalid || undefined}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Input
				id={id}
				{...input}
				name={field.name}
				ref={field.ref}
				value={field.value ?? ""}
				onChange={field.onChange}
				onBlur={field.onBlur}
				disabled={field.disabled || input.disabled}
				aria-invalid={fieldState.invalid || undefined}
				aria-describedby={describedBy || undefined}
			/>
			{description ? <FieldDescription id={`${id}-description`}>{description}</FieldDescription> : null}
			<FieldError id={`${id}-error`}>{fieldState.error?.message}</FieldError>
		</Field>
	);
}

/** A labelled multi-line text input bound to one field (a markdown body, an answer). */
export function TextareaField<T extends FieldValues>({
	control,
	name,
	label,
	description,
	...textarea
}: {
	control: Control<T, unknown, unknown>;
	name: FieldPath<T>;
	label: ReactNode;
	description?: ReactNode;
} & Omit<ComponentProps<"textarea">, "name" | "value" | "defaultValue" | "onChange" | "onBlur">) {
	const { field, fieldState } = useController({ control, name });
	const id = useId();
	const describedBy = [description ? `${id}-description` : "", fieldState.error ? `${id}-error` : ""]
		.filter(Boolean)
		.join(" ");

	return (
		<Field data-invalid={fieldState.invalid || undefined}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Textarea
				id={id}
				{...textarea}
				name={field.name}
				ref={field.ref}
				value={field.value ?? ""}
				onChange={field.onChange}
				onBlur={field.onBlur}
				disabled={field.disabled || textarea.disabled}
				aria-invalid={fieldState.invalid || undefined}
				aria-describedby={describedBy || undefined}
			/>
			{description ? <FieldDescription id={`${id}-description`}>{description}</FieldDescription> : null}
			<FieldError id={`${id}-error`}>{fieldState.error?.message}</FieldError>
		</Field>
	);
}

/** An on/off field (published) as a switch, with its label and help text beside it. */
export function SwitchField<T extends FieldValues>({
	control,
	name,
	label,
	description,
}: {
	control: Control<T, unknown, unknown>;
	name: FieldPath<T>;
	label: ReactNode;
	description?: ReactNode;
}) {
	const { field } = useController({ control, name });
	const id = useId();
	return (
		<Field className="flex-row items-start gap-3">
			<Switch
				id={id}
				name={field.name}
				inputRef={field.ref}
				checked={Boolean(field.value)}
				onCheckedChange={(checked) => field.onChange(checked)}
				onBlur={field.onBlur}
				disabled={field.disabled}
				aria-labelledby={`${id}-label`}
				aria-describedby={description ? `${id}-description` : undefined}
				className="mt-0.5"
			/>
			<div className="flex flex-col gap-1">
				<FieldLabel id={`${id}-label`} htmlFor={id}>
					{label}
				</FieldLabel>
				{description ? <FieldDescription id={`${id}-description`}>{description}</FieldDescription> : null}
			</div>
		</Field>
	);
}

/**
 * A box to tick for an explicit confirmation ("Ik heb het btw-nummer zelf gecontroleerd"), with its
 * label and help text beside it, and an error under it (unlike a switch, a missing tick is an error
 * the form can show).
 */
export function CheckboxField<T extends FieldValues>({
	control,
	name,
	label,
	description,
}: {
	control: Control<T, unknown, unknown>;
	name: FieldPath<T>;
	label: ReactNode;
	description?: ReactNode;
}) {
	const { field, fieldState } = useController({ control, name });
	const id = useId();
	const describedBy = [description ? `${id}-description` : "", fieldState.error ? `${id}-error` : ""]
		.filter(Boolean)
		.join(" ");
	return (
		<Field className="flex-row items-start gap-3" data-invalid={fieldState.invalid || undefined}>
			<input
				type="checkbox"
				id={id}
				name={field.name}
				ref={field.ref}
				checked={Boolean(field.value)}
				onChange={(event) => field.onChange(event.target.checked)}
				onBlur={field.onBlur}
				disabled={field.disabled}
				aria-invalid={fieldState.invalid || undefined}
				aria-describedby={describedBy || undefined}
				className="mt-0.5 size-4 shrink-0 accent-primary"
			/>
			<div className="flex flex-col gap-1">
				<FieldLabel htmlFor={id}>{label}</FieldLabel>
				{description ? <FieldDescription id={`${id}-description`}>{description}</FieldDescription> : null}
				<FieldError id={`${id}-error`}>{fieldState.error?.message}</FieldError>
			</div>
		</Field>
	);
}

/** A labelled choice from a short list, bound to one field; the value is the option's string. */
export function SelectField<T extends FieldValues>({
	control,
	name,
	label,
	options,
	className,
}: {
	control: Control<T, unknown, unknown>;
	name: FieldPath<T>;
	label: ReactNode;
	options: readonly { readonly value: string; readonly label: string }[];
	className?: string;
}) {
	const { field, fieldState } = useController({ control, name });
	const id = useId();
	return (
		<Field data-invalid={fieldState.invalid || undefined} className={className}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<NativeSelect
				id={id}
				name={field.name}
				ref={field.ref}
				value={String(field.value ?? "")}
				onChange={field.onChange}
				onBlur={field.onBlur}
				aria-invalid={fieldState.invalid || undefined}
			>
				{options.map((option) => (
					<NativeSelectOption key={option.value} value={option.value}>
						{option.label}
					</NativeSelectOption>
				))}
			</NativeSelect>
			<FieldError>{fieldState.error?.message}</FieldError>
		</Field>
	);
}

/** The error that belongs to no field (`form.setError("root", ...)`), above the buttons. */
export function FormError<T extends FieldValues>({ control }: { control: Control<T, unknown, unknown> }) {
	const { errors } = useFormState({ control });
	const message = errors.root?.message;
	if (!message) return null;
	return (
		<Alert variant="destructive">
			<IconAlertCircle aria-hidden />
			<AlertDescription>{message}</AlertDescription>
		</Alert>
	);
}

/** Submits the form; while it is busy it says so and cannot be pressed twice. */
export function SubmitButton<T extends FieldValues>({
	control,
	children,
	...props
}: { control: Control<T, unknown, unknown> } & ComponentProps<typeof Button>) {
	const { t } = useTranslation();
	const { isSubmitting } = useFormState({ control });
	return (
		<Button type="submit" disabled={isSubmitting} {...props}>
			{isSubmitting ? <IconLoader2 className="animate-spin" aria-hidden /> : null}
			{isSubmitting ? t("form.saving") : children}
		</Button>
	);
}
